import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import {
  getAccessToken,
  getRefreshToken,
  getUserInfo,
  setLocalAccessToken,
  setLocalRefreshToken,
  setLocalUserInfo,
  clearLocalStorage,
  getTenantId,
  setLocalTenantId,
} from "@/lib/storeManager";
import {
  refreshAccessToken,
  ACCESS_TOKEN_REFRESH_INTERVAL_MS,
} from "@/lib/tokenRefresh";
import { authService, SignupDetailsPayload } from "../services/auth.service";
import { jwtDecode } from "jwt-decode";
import { toast } from "sonner";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  setCredentials,
  clearCredentials,
  updateUser,
} from "@/store/slices/authSlice";

export interface UserProfile {
  first_name?: string;
  last_name?: string;
  email?: string;
  date_of_birth?: string;
  gender?: string;
  phone_number?: string;
  username?: string;
  [key: string]: any;
}

interface AuthContextType {
  user: UserProfile | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  requestSignupOtp: (details: SignupDetailsPayload) => Promise<string>;
  verifySignupOtp: (
    otp: string,
    codeReference: string,
    details?: SignupDetailsPayload
  ) => Promise<void>;
  requestLoginOtp: (phoneNumber: string) => Promise<string>;
  verifyLoginOtp: (otp: string, codeReference: string) => Promise<void>;
  logout: () => void;
  requireAuth: (action: () => void, currentPath?: string) => boolean;
  updateSessionUser: (userInfo: UserProfile) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const dispatch = useAppDispatch();
  const user = useAppSelector(state => state.auth.user);
  const accessToken = useAppSelector(state => state.auth.accessToken);
  const refreshToken = useAppSelector(state => state.auth.refreshToken);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Initialize session from LocalStorage if not already rehydrated
  useEffect(() => {
    try {
      const storedToken = getAccessToken();
      const storedRefresh = getRefreshToken();
      const storedUser = getUserInfo();

      if (storedToken && storedUser && !accessToken) {
        dispatch(
          setCredentials({
            user: storedUser,
            accessToken: storedToken,
            refreshToken: storedRefresh,
          })
        );
      }
    } catch (err) {
      console.error("Failed to restore auth session", err);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, dispatch]);

  const saveSession = (
    token: string,
    refresh: string | null,
    userInfo: UserProfile
  ) => {
    dispatch(
      setCredentials({
        user: userInfo,
        accessToken: token,
        refreshToken: refresh,
      })
    );
    setLocalAccessToken(token);
    if (refresh) {
      setLocalRefreshToken(refresh);
    }
    setLocalUserInfo(userInfo);
  };

  const logout = useCallback(() => {
    const preservedTenant = getTenantId();
    dispatch(clearCredentials());
    clearLocalStorage();
    if (preservedTenant) {
      setLocalTenantId(preservedTenant);
    }
    toast.success("Successfully logged out");
  }, [dispatch]);

  const requestSignupOtp = async (
    details: SignupDetailsPayload
  ): Promise<string> => {
    const data = await authService.requestSignupOtp(details);
    const codeRef =
      data?.code_reference ??
      data?.data?.code_reference ??
      (data as any)?.code_ref;
    if (!codeRef) {
      throw new Error("Failed to receive code reference from server");
    }
    return codeRef;
  };

  const verifySignupOtp = async (
    otp: string,
    codeReference: string,
    details?: SignupDetailsPayload
  ): Promise<void> => {
    const res = await authService.verifySignupOtp(otp, codeReference);
    const token =
      res?.access ?? res?.token ?? res?.data?.access ?? `token_${Date.now()}`;
    const refresh = res?.refresh ?? res?.data?.refresh ?? null;
    const userProfile: UserProfile = {
      ...(details
        ? {
            first_name: details.first_name,
            last_name: details.last_name,
            email: details.email,
            date_of_birth: details.date_of_birth,
            gender: details.gender,
            phone_number: details.phone_number,
            username: details.username,
          }
        : {}),
      ...(res?.user ?? res?.data?.user ?? {}),
    };

    saveSession(token, refresh, userProfile);
    toast.success("Account created successfully!");
  };

  const requestLoginOtp = async (phoneNumber: string): Promise<string> => {
    const data = await authService.requestLoginOtp(phoneNumber);
    const codeRef =
      data?.code_reference ??
      data?.data?.code_reference ??
      (data as any)?.code_ref;
    if (!codeRef) {
      throw new Error("Failed to receive code reference from server");
    }
    return codeRef;
  };

  const verifyLoginOtp = async (
    otp: string,
    codeReference: string
  ): Promise<void> => {
    const res = await authService.verifyLoginOtp(otp, codeReference);
    const token =
      res?.access ?? res?.token ?? res?.data?.access ?? `token_${Date.now()}`;
    const refresh = res?.refresh ?? res?.data?.refresh ?? null;
    const userProfile: UserProfile = res?.user ??
      res?.data?.user ?? {
        first_name: "Donor",
        phone_number: res?.phone_number,
      };

    saveSession(token, refresh, userProfile);
    toast.success("Logged in successfully!");
  };

  const isAuthenticated = Boolean(accessToken && user);

interface DecodedToken {
  userId?: string;
  email?: string;
  exp: number;
  iat?: number;
  [key: string]: any;
}

  const scheduleRefresh = useCallback(() => {
    if (!refreshToken || !accessToken) {
      return;
    }

    const runRefresh = async () => {
      try {
        const tokens = await refreshAccessToken();
        const access = tokens.access;
        const refresh = tokens.refresh ?? refreshToken;
        const storedUser = getUserInfo();
        if (storedUser) {
          dispatch(
            setCredentials({
              user: storedUser,
              accessToken: access,
              refreshToken: refresh,
            })
          );
        }
      } catch (err) {
        console.error("Token refresh failed, logging out", err);
        logout();
      }
    };

    const checkRefresh = () => {
      try {
        const decoded = jwtDecode<DecodedToken>(accessToken);
        const expMs = decoded.exp * 1000;
        const iatMs = (decoded.iat ?? (decoded.exp - 15 * 60)) * 1000;
        const nowMs = Date.now();
        const totalDuration = expMs - iatMs;
        const buffer = Math.max(5 * 60 * 1000, totalDuration * 0.1);

        if (expMs - nowMs <= buffer) {
          void runRefresh();
        }
      } catch (err) {
        console.error("Failed to check token expiry", err);
      }
    };

    // Run check immediately
    checkRefresh();

    const intervalId = window.setInterval(() => {
      checkRefresh();
    }, ACCESS_TOKEN_REFRESH_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [refreshToken, accessToken, logout, dispatch]);

  useEffect(() => {
    if (!isAuthenticated || !refreshToken) {
      return;
    }
    const cleanup = scheduleRefresh();
    return cleanup;
  }, [isAuthenticated, refreshToken, scheduleRefresh]);

  const requireAuth = (action: () => void, currentPath?: string): boolean => {
    if (!isAuthenticated) {
      const redirectTarget = currentPath ?? window.location.pathname;
      toast.info("Please log in to continue with your donation");
      window.location.href = `/login?redirectTo=${encodeURIComponent(redirectTarget)}`;
      return false;
    }
    action();
    return true;
  };

  const updateSessionUser = useCallback(
    (userInfo: UserProfile) => {
      dispatch(updateUser(userInfo));
      if (user) {
        setLocalUserInfo({ ...user, ...userInfo });
      }
    },
    [dispatch, user]
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        accessToken,
        refreshToken,
        isAuthenticated,
        isLoading,
        requestSignupOtp,
        verifySignupOtp,
        requestLoginOtp,
        verifyLoginOtp,
        logout,
        requireAuth,
        updateSessionUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
